<template>
  <div
    v-if="isVisible"
    class="recipe-popup-overlay"
    @click="handleOverlayClick"
  >
    <div class="recipe-popup-content" @click.stop>
      <!-- Close Button -->
      <button
        class="recipe-popup-close"
        @click="$emit('close')"
        :aria-label="$t('recipe.popup.close')"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <!-- Recipe Content -->
      <div class="recipe-popup-body">
        <!-- Recipe Image -->
        <div class="recipe-image-container">
          <ImageWrapper
            :src="recipe.image"
            :alt="recipe.title"
            class="recipe-image"
          />
        </div>

        <!-- Recipe Details -->
        <div class="recipe-details">
          <h2 class="recipe-title">{{ recipe.title }}</h2>
          <p class="recipe-description">{{ recipe.description }}</p>

          <!-- Recipe Meta Info -->
          <div class="recipe-meta">
            <div class="recipe-meta-item">
              <span class="recipe-meta-label"
                >{{ $t("recipe.popup.servings") }}:</span
              >
              <span class="recipe-meta-value"
                >{{ recipe.servings }} {{ $t("recipe.popup.people") }}</span
              >
            </div>
            <div class="recipe-meta-item">
              <span class="recipe-meta-label"
                >{{ $t("recipe.popup.prepTime") }}:</span
              >
              <span class="recipe-meta-value"
                >{{ recipe.prepTime }} {{ $t("recipe.popup.minutes") }}</span
              >
            </div>
          </div>

          <!-- Ingredients Section -->
          <div class="recipe-section">
            <h3 class="recipe-section-title">
              {{ $t("recipe.popup.ingredients") }}
            </h3>
            <ul class="recipe-ingredients">
              <li
                v-for="(ingredient, index) in recipe.ingredients"
                :key="index"
                class="recipe-ingredient"
              >
                {{ ingredient }}
              </li>
            </ul>
          </div>

          <!-- Instructions Section -->
          <div class="recipe-section">
            <h3 class="recipe-section-title">
              {{ $t("recipe.popup.instructions") }}
            </h3>
            <ol class="recipe-instructions">
              <li
                v-for="(instruction, index) in recipe.instructions"
                :key="index"
                class="recipe-instruction"
              >
                {{ instruction }}
              </li>
            </ol>
          </div>

          <!-- Tips Section (Optional) -->
          <div
            v-if="recipe.tips && recipe.tips.length > 0"
            class="recipe-section"
          >
            <h3 class="recipe-section-title">{{ $t("recipe.popup.tips") }}</h3>
            <ul class="recipe-tips">
              <li
                v-for="(tip, index) in recipe.tips"
                :key="index"
                class="recipe-tip"
              >
                {{ tip }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ImageWrapper from "../atoms/ImageWrapper.vue";

export default {
  name: "RecipeHomePopup",
  components: {
    ImageWrapper,
  },
  props: {
    /**
     * Controls the visibility of the popup
     */
    isVisible: {
      type: Boolean,
      default: false,
    },
    /**
     * Recipe data object containing all recipe information
     */
    recipe: {
      type: Object,
      required: true,
      default: () => ({
        title: "",
        description: "",
        image: "",
        servings: 1,
        prepTime: 0,
        ingredients: [],
        instructions: [],
        tips: [],
      }),
    },
  },
  mounted() {
    // Add event listener for escape key
    document.addEventListener("keydown", this.handleEscapeKey);
  },
  beforeDestroy() {
    // Remove event listener when component is destroyed
    document.removeEventListener("keydown", this.handleEscapeKey);
  },
  methods: {
    /**
     * Handles escape key press to close popup
     */
    handleEscapeKey(event) {
      if (event.key === "Escape" && this.isVisible) {
        this.$emit("close");
      }
    },
    /**
     * Handles clicking on the overlay background to close popup
     */
    handleOverlayClick() {
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/scss/include/_commonInclude.scss";

.content .app {
  .recipe-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(3px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    animation: fadeIn 0.3s ease-out;
    padding: rem(20);
  }

  .recipe-popup-content {
    background-color: color(white);
    border-radius: rem(16);
    box-shadow: 0 rem(12) rem(48) rgba(0, 0, 0, 0.2);
    width: 100%;
    max-width: rem(800);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    position: relative;
    animation: slideUp 0.3s ease-out;
    overflow: hidden;

    @include media-breakpoint-down(md) {
      max-width: 95%;
      border-radius: rem(12);
    }
  }

  .recipe-popup-close-btn {
    background: color(white);
    border: 1px solid color(lightBorderGray);
    padding: rem(8);
    cursor: pointer;
    color: color(mediumGray);
    border-radius: rem(8);
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: rem(40);
    height: rem(40);
    position: absolute;
    top: rem(16);
    right: rem(16);
    z-index: 10;
    box-shadow: 0 rem(2) rem(8) rgba(0, 0, 0, 0.1);

    &:hover {
      background-color: color(lightGray);
      color: color(darkText);
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }

    svg {
      width: rem(20);
      height: rem(20);
    }
  }

  .recipe-popup-body {
    flex-grow: 1;
    overflow-y: auto;
    padding: rem(24);
    padding-top: rem(60);

    @include media-breakpoint-down(md) {
      padding: rem(16);
      padding-top: rem(60);
    }

    &::-webkit-scrollbar {
      width: rem(6);
    }

    &::-webkit-scrollbar-track {
      background: color(lightGray);
      border-radius: rem(3);
    }

    &::-webkit-scrollbar-thumb {
      background: color(mediumGray);
      border-radius: rem(3);

      &:hover {
        background: color(darkGray);
      }
    }
  }

  .recipe-header {
    display: flex;
    gap: rem(24);
    margin-bottom: rem(32);

    @include media-breakpoint-down(md) {
      flex-direction: column;
      gap: rem(16);
      margin-bottom: rem(24);
    }
  }

  .recipe-image-container {
    flex-shrink: 0;
    width: rem(200);
    height: rem(150);
    border-radius: rem(12);
    overflow: hidden;
    box-shadow: 0 rem(4) rem(16) rgba(0, 0, 0, 0.1);

    @include media-breakpoint-down(md) {
      width: 100%;
      height: rem(200);
    }
  }

  .recipe-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .recipe-info {
    flex: 1;
  }

  .recipe-title {
    font-size: rem(28);
    font-weight: 700;
    color: color(darkText);
    margin-bottom: rem(8);
    line-height: 1.2;

    @include media-breakpoint-down(md) {
      font-size: rem(24);
    }
  }

  .recipe-description {
    font-size: rem(16);
    color: color(mediumGray);
    margin-bottom: rem(16);
    line-height: 1.5;
  }

  .recipe-meta {
    display: flex;
    gap: rem(24);

    @include media-breakpoint-down(md) {
      flex-direction: column;
      gap: rem(8);
    }
  }

  .recipe-meta-item {
    display: flex;
    align-items: center;
    gap: rem(8);
  }

  .recipe-meta-label {
    font-size: rem(14);
    font-weight: 600;
    color: color(nespressoBrown);
    text-transform: uppercase;
    letter-spacing: rem(0.5);
  }

  .recipe-meta-value {
    font-size: rem(14);
    font-weight: 500;
    color: color(darkText);
  }

  .recipe-section {
    margin-bottom: rem(24);

    &:last-child {
      margin-bottom: 0;
    }
  }

  .recipe-section-title {
    font-size: rem(20);
    font-weight: 700;
    color: color(nespressoBrown);
    margin-bottom: rem(12);
    padding-bottom: rem(8);
    border-bottom: 2px solid color(lightBorderGray);
  }

  .recipe-ingredients-list,
  .recipe-tips-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .recipe-ingredient-item,
  .recipe-tip-item {
    position: relative;
    padding-left: rem(20);
    margin-bottom: rem(8);
    font-size: rem(15);
    color: color(darkText);
    line-height: 1.5;

    &:before {
      content: "•";
      position: absolute;
      left: 0;
      color: color(nespressoBrown);
      font-weight: 700;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .recipe-instructions-list {
    list-style: none;
    padding: 0;
    margin: 0;
    counter-reset: instruction-counter;
  }

  .recipe-instruction-item {
    position: relative;
    padding-left: rem(32);
    margin-bottom: rem(12);
    font-size: rem(15);
    color: color(darkText);
    line-height: 1.5;
    counter-increment: instruction-counter;

    &:before {
      content: counter(instruction-counter);
      position: absolute;
      left: 0;
      top: 0;
      width: rem(24);
      height: rem(24);
      background-color: color(nespressoBrown);
      color: color(white);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: rem(12);
      font-weight: 700;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(rem(30)) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
}
</style>
